<template>
  <div id="app">
    <nav class="navbar has-shadow">
      <template v-if="user">
        <div class="navbar-brand">
          <router-link to="/" class="navbar-item">
            <img class="is-hidden-mobile" src="./assets/logo1.png" alt="Me Want Food"> 
            <img class="is-hidden-tablet" width="85px" src="./assets/logo1.png" alt="Me Want Food"> 
          </router-link>

          <!-- https://stackoverflow.com/questions/37838173/vuejs-application-with-different-layouts-e-g-login-layout-page-laypit-signup -->
          <!-- 2 different inputs, that's probably bad? -->
          <div class="navbar-item search-size">
            <search-bar
              @getMatches="onGetMatches"
              @match="onSearchMatch"
              :matchesFound="typeaheadResults"
              :placeholder="$t('search')">
            </search-bar>
          </div>

          <div class="navbar-burger burger" v-on:click="toggleNav" v-bind:class="{ 'is-active': burgerIsActive }">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>

        <div id="appNavBar" class="navbar-menu" v-bind:class="{ 'is-active': burgerIsActive }">
          <div class="navbar-start">
            <div class="navbar-item has-dropdown is-hoverable is-hidden-desktop">
              <p class="navbar-link">{{$t('welcome')}}&nbsp;<strong>{{user.firstName}}</strong></p>
              <div class="navbar-dropdown">
                <a v-if="locale !== 'en'" role="button" class="navbar-item" @click="setLocale('en')">
                  <span class="menu-flag-icon">
                    <span class="flag-icon flag-icon-us"></span>
                  </span>
                  English
                </a>
                <a v-if="locale !== 'es'" role="button" class="navbar-item" @click="setLocale('es')">
                  <span class="menu-flag-icon">
                    <span class="flag-icon flag-icon-ec"></span>
                  </span>
                  Español
                </a>
                <a class="navbar-item" @click="onLogout">
                  <b-icon class="menu-icon" icon="sign-out-alt"/>
                  {{$t('logout')}}
                </a>
              </div>
            </div>
            <hr class="dropdown-divider is-hidden-desktop"/>

            <!-- Pull in toolbar component based on route -->
            <template v-if="$route.name === 'browse'">
              <browse-app-toolbar></browse-app-toolbar>
            </template>
            <template v-else-if="$route.name === 'shoppingList'">
              <shopping-list-app-toolbar></shopping-list-app-toolbar>
            </template>
            <template v-else>
              <new-app-toolbar></new-app-toolbar>
            </template>
          </div>

          <div class="navbar-end is-hidden-touch">
            <div class="navbar-item has-dropdown is-hoverable">
              <p class="navbar-link">{{$t('welcome')}}&nbsp;<strong>{{user.firstName}}</strong></p>
              <div class="navbar-dropdown">
                <a v-if="locale !== 'en'" role="button" class="navbar-item" @click="setLocale('en')">
                  <span class="menu-flag-icon">
                    <span class="flag-icon flag-icon-us"></span>
                  </span>
                  English
                </a>
                <a v-if="locale !== 'es'" role="button" class="navbar-item" @click="setLocale('es')">
                  <span class="menu-flag-icon">
                    <span class="flag-icon flag-icon-ec"></span>
                  </span>
                  Español
                </a>
                <a class="navbar-item" @click="onLogout">
                  <b-icon class="menu-icon" icon="sign-out-alt"/>
                  {{$t('logout')}}
                </a>
              </div>
            </div>
          </div>
        </div>
      </template>
    </nav>

<!--
    <loading-modal :isActive="!initDataLoaded" :message="loadingMessage"></loading-modal>
-->
    <div class="section">
      <transition :name="transitionName" mode="out-in">
        <router-view/>
      </transition>
    </div>

    <footer class="footer is-primary">
      <div class="container">
        <div class="content has-text-centered">
          <p><strong>Me Want Food</strong> by David Goldbaum.</p>
        </div>
      </div>
    </footer>

    <div id="mwfIsMobile" class="is-hidden-tablet"/>
  </div>
</template>

<i18n src="./i18n/App.i18n.json"></i18n>

<script>
import Vue from 'vue';
import { mapGetters, mapMutations } from 'vuex';
import routingMixin from '@/mixins/routing';
import i18nMixin from '@/mixins/i18n';
import Loading from '@/components/Loading';
import SearchBar from '@/components/SearchBar';
import ShoppingListAppToolbar from '@/components/shopping-list/ShoppingListAppToolbar';
import BrowseAppToolbar from '@/components/browse/BrowseAppToolbar';
import NewAppToolbar from '@/components/NewAppToolbar';
import { logout } from './utils/auth';
import Types from './foodItem-types';

Vue.mixin({
  methods: {
    isMobile() {
      return window.getComputedStyle(window.mwf.isMobileEl, null)
        .getPropertyValue('display') !== 'none';
    },
  },
});

export default {
  name: 'app',
  mixins: [i18nMixin, routingMixin],
  data() {
    return {
      burgerIsActive: false,
      loadingMessage: 'Loading...',
      transitionName: 'slide-left',
    };
  },
  mounted() {
    window.mwf = { isMobileEl: document.getElementById('mwfIsMobile') };
  },
  watch: {
    user() {
      if (this.user) {
        this.burgerIsActive = false;
      }
    },
    $route(to, from) {
      this.burgerIsActive = false;

      if (Types.getTypeFromRoute(to)) {
        this.transitionName = 'slide-left';
      } else if (Types.getTypeFromRoute(from)) {
        this.transitionName = 'slide-right';
      } else {
        this.transitionName = null;
      }
    },
  },
  methods: {
    ...mapMutations({ setLocale: 'locale' }),
    toggleNav() {
      this.burgerIsActive = !this.burgerIsActive;
    },
    onGetMatches(text) {
      this.burgerIsActive = false;
      this.$store.dispatch('typeaheadQuery', text)
        /* eslint-disable no-console */
        .catch(err => console.error(err));
    },
    onSearchMatch(value) {
      if (value.filter) {
        this.routeBrowse({ categories: value.filter, component: this });
      } else if (value.id) {
        this.routeFoodItem(value);
      }
    },
    onLogout() {
      logout();
      this.$router.push({ name: 'login' });
    },
  },
  computed: {
    ...mapGetters([
      'user',
      'initDataLoaded',
      'typeaheadResults',
      'locale',
    ]),
  },
  components: {
    'loading-modal': Loading,
    'search-bar': SearchBar,
    ShoppingListAppToolbar,
    BrowseAppToolbar,
    NewAppToolbar,
  },
};

</script>

<style lang="sass">
@import '../node_modules/bulma/bulma.sass'
@import '../node_modules/flag-icon-css/css/flag-icon.min.css'

.welcome
  padding-top: 0.5rem
  padding-right: 0.5rem

.search-size
  flex: auto
  max-width: 230px

.navbar
  .navbar-start a
    font-weight: bold

  .navbar-end a
    color: #C1C1C1
    padding: 0.5rem 1.75rem
    font-weight: bold
    +mobile
      color: gray
      &:hover
        background-color: #F1F1F1

  footer
    background-color: $primary !important
    color: #fff

    .icon
      color: #fff
      margin-left: 20px

.menu-icon
  padding-right: 1rem

.menu-flag-icon
  padding-right: 0.7rem
  margin-left: -0.4rem

// Fix nav-link in navbar-end, for some reason it's light gray
.navbar .navbar-end a
  color: #0A0A0A

.slide-left-enter, .slide-right-leave-to
  transform: translate(100%, 0)

.slide-left-leave-to, .slide-right-enter
  transform: translate(-100%, 0)

.slide-left-leave-active, .slide-right-leave-active
  transition: 0.1s

.slide-left-enter-active, .slide-right-enter-active
  transition: 0.3s

</style>



// WEBPACK FOOTER //
// src/App.vue